import type { ContactSaveResponse } from "@/modules/common/utils/contact-merge";
import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { ClientSearchFilters } from "@/modules/clients/types";
import { EnvironmentVariables } from "@/modules/common/types";
import { JourneysResponse } from "@/modules/leads/dtos/index.v2";
import { AxiosResponse } from "axios";
import { UUID } from "crypto";
import {
  AddEmailDTO,
  AddTelephoneDTO,
  CreateCustomerLeadDTO,
  CustomerDTO,
  CustomersResponse,
  UpdateEmailDTO,
  UpdateTelephoneDTO,
} from "../dtos";

export const addCustomerTelephone = async (
  customerId: string | undefined,
  data: AddTelephoneDTO
): Promise<ContactSaveResponse> => {
  if (!customerId) {
    throw new Error("Customer ID is required");
  }

  const apiUrl = brandUrl<AxiosResponse<ContactSaveResponse>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerId}/customer-telephones`
  );

  const response = await fetchFromBrandedUrl<AxiosResponse<ContactSaveResponse>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
  return response.data;
};

export const updateCustomerTelephone = async (
  telephoneID: string | undefined,
  data: UpdateTelephoneDTO
): Promise<CustomerDTO> => {
  const apiUrl = brandUrl<CustomerDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customer-telephones/${telephoneID}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const deleteCustomerTelephone = async (
  telephoneID: string | undefined
): Promise<CustomerDTO> => {
  const apiUrl = brandUrl<CustomerDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customer-telephones/${telephoneID}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const addCustomerEmail = async (
  customerId: string | undefined,
  data: AddEmailDTO
): Promise<CustomerDTO> => {
  const apiUrl = brandUrl<CustomerDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerId}/customer-emails`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const updateCustomerEmail = async (
  emailID: string | undefined,
  data: UpdateEmailDTO
): Promise<CustomerDTO> => {
  const apiUrl = brandUrl<CustomerDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customer-emails/${emailID}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const getCustomerByUId = async ({
  uid,
}: {
  uid?: string;
}): Promise<AxiosResponse<CustomerDTO> | null> => {
  if (!uid) {
    return null;
  }

  const apiUrl = brandUrl<Promise<AxiosResponse<CustomerDTO>>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${uid}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getCustomerJourneys = async ({
  filters,
  pagination,
  organizationUIds,
}: {
  organizationUIds?: string;
  filters: {
    journeyUIds?: string;
  };
  pagination: {
    page: number;
    pageSize: number;
    orderBy: string;
    orderDirection: string;
  };
}) => {
  const apiUrl = brandUrl<Promise<JourneysResponse>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...filters,
        ...pagination,
        organizationUIds,
      },
    },
  });
};

export const createCustomerLead = async ({
  data,
  signal,
}: {
  data: CreateCustomerLeadDTO;
  signal?: AbortSignal;
}): Promise<CustomerDTO> => {
  const apiUrl = brandUrl<CustomerDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      signal,
      data,
    },
  });
};

export const getCustomers = async ({
  filters,
  pagination,
  organizationUIds,
}: {
  organizationUIds?: string;
  filters: ClientSearchFilters;
  pagination: {
    page: number;
    pageSize: number;
    orderCustomerBy: string;
    orderDirection: string;
  };
}) => {
  const apiUrl = brandUrl<Promise<CustomersResponse>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...filters,
        ...pagination,
        organizationUIds,
      },
    },
  });
};

export const exportCustomers = async ({
  filters,
}: {
  filters: ClientSearchFilters;
}) => {
  const apiUrl = brandUrl<{
    url: string;
  }>(`${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/export-customers`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { ...filters },
    },
  });
};

export const exportSpecificCustomers = async ({
  customerUIds,
}: {
  customerUIds: UUID[];
}) => {
  const apiUrl = brandUrl<{
    url: string;
  }>(`${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/export-by-ids`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { customerUIds },
    },
  });
};

export const bulkCustomerUpload = async (formData: FormData) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/import-customers`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      formData: true,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  });
};
